import React, { useCallback, useEffect, useRef, useState } from 'react';
import http from '../../services/http';
import LoadingIndicator from '../../common/Loading';
import ServiceInterruptionOverlay from './ServiceInterruptionOverlay';
import { useServiceInterruptionRetry } from '../../hooks/useServiceInterruptionRetry';
import {
  endSessionAndRedirect,
  getHttpStatus,
  getStoredAuthToken,
  isJwtExpired,
  isTransientServerError,
  LOGIN_REASONS,
  shouldEndSessionForAuthError,
} from '../../utils/sessionAuth';

interface SessionGateProps {
  children: React.ReactNode;
}

type GateState = 'pass_through' | 'checking' | 'valid' | 'unavailable';

const SessionGate: React.FC<SessionGateProps> = ({ children }) => {
  const [state, setState] = useState<GateState>('checking');
  const [checkGeneration, setCheckGeneration] = useState(0);
  const [retryGeneration, setRetryGeneration] = useState(0);
  const [outageRecovery, setOutageRecovery] = useState(false);
  const outageRecoveryRef = useRef(false);

  const validateSession = useCallback(async (signal?: AbortSignal) => {
    const token = getStoredAuthToken();
    if (!token) {
      outageRecoveryRef.current = false;
      setOutageRecovery(false);
      setState('pass_through');
      return;
    }

    if (isJwtExpired(token)) {
      endSessionAndRedirect(LOGIN_REASONS.session_expired);
      return;
    }

    if (!outageRecoveryRef.current) {
      setState('checking');
    }
    try {
      await http.get('/api/auth/session', { signal });
      outageRecoveryRef.current = false;
      setOutageRecovery(false);
      setState('valid');
    } catch (error: unknown) {
      const status = getHttpStatus(error);
      if (shouldEndSessionForAuthError(error, '/api/auth/session')) {
        endSessionAndRedirect(LOGIN_REASONS.session_expired);
        return;
      }
      if (isTransientServerError(status) || !status) {
        outageRecoveryRef.current = true;
        setOutageRecovery(true);
        setState('unavailable');
        return;
      }
      outageRecoveryRef.current = false;
      setOutageRecovery(false);
      setState('valid');
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void validateSession(controller.signal);
    return () => controller.abort();
  }, [validateSession, checkGeneration]);

  const triggerRetry = useCallback(() => {
    setRetryGeneration((n) => n + 1);
    setCheckGeneration((n) => n + 1);
  }, []);

  const interruption = useServiceInterruptionRetry({
    isActive: outageRecovery,
    retryGeneration,
    onRetry: triggerRetry,
  });

  if (state === 'pass_through' || state === 'valid') {
    return <>{children}</>;
  }

  if (state === 'checking' && !outageRecovery) {
    return <LoadingIndicator />;
  }

  if (outageRecovery) {
    return (
      <ServiceInterruptionOverlay
        secondsRemaining={interruption.secondsRemaining}
        totalSeconds={interruption.stage.retryDelaySeconds}
        title={interruption.stage.title}
        message={interruption.stage.message}
        showProgressBar={interruption.stage.showProgressBar}
        showCountdown={interruption.showCountdown}
        onReturnToLogin={interruption.returnToLogin}
        secondaryAction={{ label: 'Try again', onClick: interruption.retryNow }}
      />
    );
  }

  return <LoadingIndicator />;
};

export default SessionGate;
